import { Component, AfterViewInit, ViewChild, Inject } from "@angular/core";
import { FormGroup, FormControl } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { LxmMessage, LxmDialog } from "src/app/_helpers";
import { nextSchemeDay } from "src/app/_helpers/calculate";
import { DialogComponent } from "src/app/components";
import { IClvDto, IRetailChain } from "../../product-info/product-info.model";
import { ProductStoreService } from "src/app/_services/product-store.service";
import { ProductStoreCard } from "../product-store.card";
import { ProductPackagingCard } from "../../product-packaging/product-packaging.card";
import { today } from "src/app/_helpers/utilities";
import { LxmAppModule, Status } from "src/app/enum";
import { AppState } from "src/app/state/app.state";
import { ProductHeaderState } from "../../product-header/product-header.state";

@Component({
  selector: "product-store-schema-dialog",
  host: { class: "dialog" },
  templateUrl: "./product-store-schema.dialog.html"
})
export class ProductStoreBaseSchemaDialog implements AfterViewInit {
  @ViewChild("dialog", { static: true, read: DialogComponent })
  private _dialog: DialogComponent;

  public retailChains: IRetailChain[];
  public categoryOptions = [];

  public form: FormGroup;

  public actionLoading = {} as any;

  public isNew = false;

  public minDate = new Date().stripTimezone();

  public LxmAppModule = LxmAppModule;


  public get warehouseTypes(): IClvDto[] {
    return this.productState.dataSubject.value?.warehouseTypes || [];
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: IProductStoreSchemeDialogData,
    public dialogRef: MatDialogRef<ProductStoreBaseSchemaDialog>,
    private _productStoreService: ProductStoreService,
    private _message: LxmMessage,
    public dialog: LxmDialog,
    private _translateService: TranslateService,
    public appState: AppState,
    public productState: ProductHeaderState
  ) {
    const state = this.productState.dataSubject.value || {};

    this.retailChains = data.retailChains
      .filter((x) => !x.isClient)
      .map((x) => Object.assign({}, x));

    this.form = new FormGroup({
      id: new FormControl(),
      validFrom: new FormControl(),
      retailersListIsExclusive: new FormControl(),
      retailers: new FormControl(),
      storeCategoryId: new FormControl([]),
      warehouseTypeId: new FormControl()
    });

    this.form.patchValue(this.normalizeData(this.data.schema));

    const baseWarehouseTypeId = state?.warehouseTypeId ?? null;
    this.form
      .get("warehouseTypeId")
      ?.setValue(baseWarehouseTypeId, { emitEvent: false });
  }

  public get packagingCard(): ProductPackagingCard {
    return this.data.packagingCard;
  }

  public get packagingData(): any[] {
    return this.packagingCard.dataSource.data;
  }

  public get minValidFrom(): Date {
    return this.nextDay(this.data.schema?.validFrom?.editableDate);
  }

  public nextDay(date = null) {
    return this.data.hasAnyScheme == false ? today() : nextSchemeDay(date);
  }

  public openPackagingDialog(row: any): void {
    this.packagingCard.openPackagingDialog(row);
  }

  public get isTodaySelected(): boolean {
    const validFrom = this.form?.get('validFrom')?.value;
    if (!validFrom) {
      return false;
    }
    const dateObj = validFrom instanceof Date ? validFrom : new Date(validFrom);
    return dateObj.isToday();
  }

  public warehouseTypeLabel(item: any) {
      var label = item.value;
      if (item.status === Status.Disabled) {
        label += ` (${this._translateService.instant("enums.Status.Disabled")})`;
      }
      return label;
    }

  private normalizeData(data: IProductStoreSchemeDialogData): any {
    this.isNew = true;
    const existingSchema = this.data.storeSchemesCard?.storeSchemes?.[0];

    let result: any;

    if (data) {
      result = { ...data };
      result.validFrom = result.validFrom
        ? result.validFrom.editableDate
        : null;
      this.isNew = false;
    } else if (existingSchema) {
      result = { ...existingSchema };
      result.id = null;

      const minDate = result.validTo
      ? nextSchemeDay(result.validTo?.editableDate)
      : nextSchemeDay(existingSchema.validFrom?.editableDate);
      result.validFrom = minDate;
      this.minDate = minDate;
    } else {
      result = <any>{};
      result.validFrom = this.minDate;
    }
    result.retailers = result.retailerIds || result.retailers;
    result.retailersListIsExclusive = result.retailersListIsExclusive ?? true;
    return result;
  }

  ngAfterViewInit(): void {
    this._dialog.dataLoaded$.subscribe((state) => {
      if (!state) {
        return;
      }
      this.categoryOptions = this.data.formData.storeCategories;
    });
  }

  public save(): void {

    if (this.isTodaySelected) {
      this.dialog.confirm(
        {
          title: "cards.products.product_store_schemes.confirm_date_today.title",
          message: "cards.products.product_store_schemes.confirm_date_today.text",
          yes: "cards.products.product_store_schemes.confirm_date_today.yes",
          no: "cards.products.product_store_schemes.confirm_date_today.no"
        },
        () => {
          this.saveInternal();
        }
      );
    } else {
      this.saveInternal();
    }
  }

  private saveInternal(): void {
    const f = this.form.value;

    const req = {
      productId: this.data.storeSchemesCard.productId,
      validFrom: f.validFrom,
      storeCategoryId: f.storeCategoryId,
      warehouseTypeId: f.warehouseTypeId,
      retailersListIsExclusive: f.retailersListIsExclusive,
      retailerIds: f.retailers
    };

    this._productStoreService
      .save(this.data.storeSchemesCard.productId, f.id, req)
      .result(
        this.form,
        (res) => {
          this.data.storeSchemesCard.loadStoreSchemes();
          this.closeDialog();
        },
        (err) => {
          console.error(err);
          this._message.error({
            message: err.validationSummary || "common.error.save_failed"
          });
        },
        "save",
        this.actionLoading
      );
  }

  public deleteScheme() {
    this.dialog.confirm(
      {
        image: "delete-modal-image",
        template: `
      <p class="dialog-title">${this._translateService.instant("cards.products.product_store_schemes.delete.title")}</p>
      <p>
        ${this._translateService.instant("cards.products.product_store_schemes.delete.message")}
      </p>
      `
      },
      () => {
        const id = this.data.schema.id;
        this._productStoreService
          .delete(this.data.storeSchemesCard.productId, id)
          .result(
            this.form,
            () => {
              this.data.storeSchemesCard.loadStoreSchemes();
              this.closeDialog();
            },
            (err) => {
              console.error(err);
            },
            "delete",
            this.actionLoading
          );
      }
    );
  }

  public onNoClick(): void {
    this.dialogRef.close();
  }

  public closeDialog() {
    this.dialogRef.close();
  }

}

export interface IProductStoreSchemeDialogData {
  formData: any;
  storeSchemesCard: ProductStoreCard;
  retailChains: IRetailChain[];
  schema: any;
  packagingCard: ProductPackagingCard;
  hasAnyScheme: boolean;
}
